import type { TemplateManifest, EditableTypeType } from "hoxton-types";

import { EditableType } from "../enums/editables";
import { TemplateType } from "../enums/masterTemplate";
import { PREDEFINED_MANIFEST_REPORTING_LABEL_KEYS } from "../enums/manifest";
import ValidationError from "../errors/ValidationError";

import isMediaType from "./isMediaType";
import isAbsoluteUrl from "./isAbsoluteUrl";

/**
 * hoxton manifests must have
 * - a name
 * - a reporting label
 * - a valid adType of TemplateType
 * - an adSize object with width and height
 * - a platform type (not used for anything)
 * - a backup quality value (0-100)
 * - an optional metadata object with string values
 */
export function validateHoxtonData(hoxtonData: TemplateManifest): true {
    if (!hoxtonData.name) {
        throw new ValidationError(
            "name",
            hoxtonData.name,
            "Invalid JSON provided in Hoxton tags. A name must be passed in the hoxton JSON object.",
        );
    }

    if (!hoxtonData.reportingLabel) {
        throw new ValidationError(
            "reportingLabel",
            hoxtonData.reportingLabel,
            "Invalid JSON provided in Hoxton tags. A reportingLabel must be passed in the hoxton JSON object.",
        );
    }

    if (typeof hoxtonData.adSize !== "object" || hoxtonData.adSize === null) {
        throw new ValidationError(
            "adSize",
            hoxtonData.adSize,
            "Invalid JSON provided in Hoxton tags. The adSize object must be passed in the hoxton JSON object.",
        );
    }

    if (
        !hoxtonData.adSize.width ||
        isNaN(parseInt(hoxtonData.adSize.width, 10)) ||
        parseInt(hoxtonData.adSize.width, 10) <= 0 ||
        !hoxtonData.adSize.height ||
        isNaN(parseInt(hoxtonData.adSize.height, 10)) ||
        parseInt(hoxtonData.adSize.height, 10) <= 0
    ) {
        throw new ValidationError(
            "adSize",
            hoxtonData.adSize,
            "Invalid JSON provided in Hoxton tags. The adSize must be passed in the hoxton JSON object and must have a valid width and height.",
        );
    }

    if (
        !hoxtonData.adType ||
        typeof hoxtonData.adType !== "string" ||
        TemplateType[hoxtonData.adType] === undefined
    ) {
        throw new ValidationError(
            "adType",
            hoxtonData.adType,
            `Invalid JSON provided in Hoxton tags. Invalid adType provided. The "${hoxtonData.adType}" adType is not currently accepted.`,
        );
    }

    if (
        !hoxtonData.backupQuality ||
        isNaN(parseInt(hoxtonData.backupQuality, 10)) ||
        parseInt(hoxtonData.backupQuality, 10) < 0 ||
        parseInt(hoxtonData.backupQuality, 10) > 100
    ) {
        throw new ValidationError(
            "backupQuality",
            hoxtonData.backupQuality,
            "Invalid JSON provided in Hoxton tags. The backupQuality must be a number between 0 and 100.",
        );
    }

    if (Object.hasOwn(hoxtonData, "metadata")) {
        if (typeof hoxtonData.metadata !== "object" || hoxtonData.metadata === null) {
            throw new ValidationError(
                "metadata",
                hoxtonData.metadata,
                "Invalid JSON provided in Hoxton tags. The metadata property must be an object.",
            );
        }

        if (
            Object.keys(hoxtonData.metadata).some(
                (fieldName) => typeof hoxtonData.metadata![fieldName] !== "string",
            )
        ) {
            throw new ValidationError(
                "metadata",
                hoxtonData.metadata,
                "Invalid JSON provided in Hoxton tags. Metadata properties can only be strings.",
            );
        }
    }

    return true;
}

/**
 * Reporting labels follow this format: DCO_{creative}_{cta}_{width}x{height}_{language}
 * each dynamic component (the stuff between braces '{}') should be one of the following:
 * - one of the predefined keys; see PREDEFINED_MANIFEST_REPORTING_LABEL_KEYS
 * - one of the editable groups defined in the manifest or "other fields"
 * - one of the editable names defined in the manifest
 * - if it is an editable name, it must be of type EditableType.Text (its value needs to be a string)
 */
export function validateReportingLabel({
    reportingLabel,
    lowerCaseEditableGroups,
    editableNames,
    editableTypes,
}: {
    reportingLabel: string;
    lowerCaseEditableGroups: string[];
    editableNames: string[];
    editableTypes: Record<string, EditableTypeType>;
}): boolean {
    // look for components between braces '{}'
    const matches = reportingLabel.match(/{(.*?)}/g);

    return !!matches?.every((match) => {
        // remove braces '{}' from the match
        const value = match.replace(/^{|}$/g, "");
        const lowerCasedValue = value.toLowerCase();

        if (
            !value ||
            (!PREDEFINED_MANIFEST_REPORTING_LABEL_KEYS.includes(lowerCasedValue) &&
                !lowerCaseEditableGroups.includes(lowerCasedValue) &&
                !editableNames.includes(value)) ||
            (editableNames.includes(value) && editableTypes[value] !== EditableType.Text)
        ) {
            throw new ValidationError(
                "reportingLabel",
                value,
                `Invalid JSON provided in Hoxton tags. Invalid reportingLabel provided. The tag '${match}' is not known.`,
            );
        }

        return true;
    });
}
/**
 * editable rules:
 * - must be an object of this form { type: EditableType; value: string; editableGroups?: string[] }
 * - editable name (key) must be camelCase
 * - type must be one of the EditableType enum values
 * - if type is media (image, file, etc) a value must be provided
 * - if type is media value can't be an absolute URL (external URL)
 */
export function validateEditables(hoxtonData: TemplateManifest, editableNames: string[]): boolean {
    return editableNames.every((editableName) => {
        const entry = hoxtonData[editableName];

        if (typeof entry !== "object" || entry === null) {
            throw new ValidationError(
                "editables",
                entry,
                `Invalid JSON provided in Hoxton tags. Editable '${editableName}' must be an object.`,
            );
        }

        if (editableName.match(/\s|^[A-Z]/g)) {
            throw new ValidationError(
                editableName,
                undefined,
                `Invalid JSON provided in Hoxton tags. Invalid editable name provided: "${editableName}" - must be camelCase i.e. editableName`,
            );
        }

        if (!Object.values(EditableType).includes(entry.type)) {
            throw new ValidationError(
                editableName,
                entry?.type,
                `Invalid editable type provided: "${entry?.type}" for editable "${editableName}"`,
            );
        }

        if (isMediaType(entry.type) && !entry.value) {
            throw new ValidationError(
                "Media File",
                hoxtonData.metadata,
                "Invalid JSON provided in Hoxton tags. All Media type editables (image, file etc) need to have initial value.",
            );
        }

        if (isMediaType(entry.type) && isAbsoluteUrl(entry.value as string)) {
            throw new ValidationError(
                `${editableName}.type`,
                entry.type,
                `ValidationError: Invalid JSON provided in Hoxton tags. Invalid editable type provided: "${entry.type}" for editable "${editableName}" can not be an external URL (INVALID)`,
            );
        }

        return true;
    });
}
